/** @file Shift-constrain of a drag delta onto the dominant axis (x, y, plus the iso edges on an iso grid). */
import { trig } from '@pose2d/math/trig';
import { vec2 } from '@pose2d/math/vec2';
import type { Vec2 } from '@pose2d/math/types';
import { gridTileSize } from './gridTileSize';
import type { GridView } from './gridTileSize';

const XY_AXES: readonly Vec2[] = [[1, 0], [0, 1]];

/** Unit axes a constrained drag may follow on this grid. */
function axesOf(grid: GridView): readonly Vec2[] {
  if (grid.mode !== 'iso') return XY_AXES;
  const [w, h] = gridTileSize(grid);
  const n = trig.hypot(w, h);
  return n > 0 ? [...XY_AXES, [w / n, h / n], [w / n, -h / n]] : XY_AXES;
}

/** Projects (dx, dy) onto the allowed axis it is most aligned with; ties keep the earlier axis (x first). */
export function constrainDelta(grid: GridView, dx: number, dy: number, out: Vec2): Vec2 {
  let best = -1;
  let bx = 0;
  let by = 0;
  for (const [ax, ay] of axesOf(grid)) {
    const d = dx * ax + dy * ay;
    if (Math.abs(d) > best) {
      best = Math.abs(d);
      bx = ax * d;
      by = ay * d;
    }
  }
  return vec2.set(out, bx, by);
}
